import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Lightbulb,
  GraduationCap,
  MessageCircleHeart,
  BriefcaseBusiness,
  Wrench,
  PuzzleIcon,
  type LucideIcon,
} from "lucide-react";

/**
 * EcosystemCard — bespoke SVG hero illustration of the 5 Bootpreneurs pillars
 * orbiting a central Founder hub. Theme-aware, i18n-driven, reduced-motion safe.
 */

type NodeKey = "courses" | "coaching" | "fractional" | "tools" | "microservices";

const NODES: { key: NodeKey; icon: LucideIcon; angle: number }[] = [
  { key: "courses",       icon: GraduationCap,      angle: -90 },
  { key: "coaching",      icon: MessageCircleHeart, angle: -18 },
  { key: "fractional",    icon: BriefcaseBusiness,  angle: 54  },
  { key: "tools",         icon: Wrench,             angle: 126 },
  { key: "microservices", icon: PuzzleIcon,         angle: 198 },
];

const CENTER = 280;
const RADIUS = 200;
const PILL_W = 168;
const PILL_H = 52;
const HUB_R  = 60;

function polar(angleDeg: number, r: number) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: CENTER + r * Math.cos(a), y: CENTER + r * Math.sin(a) };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export function EcosystemCard() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  const nodeLabel = (k: NodeKey) => t(`home.ecosystem.nodes.${k}`);

  return (
    <div
      className="relative w-full max-w-[600px] mx-auto rounded-2xl border border-border shadow-xl overflow-hidden"
      style={{
        backgroundColor: "color-mix(in oklab, hsl(var(--card)) 85%, transparent)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      {/* dotted-grid decoration */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* metric chips */}
      <MetricChip className="top-3 left-3"     label={t("home.ecosystem.metrics.founders")} />
      <MetricChip className="top-3 right-3"    label={t("home.ecosystem.metrics.markets")} />
      <MetricChip className="bottom-3 left-3"  label={t("home.ecosystem.metrics.languages")} />
      <MetricChip className="bottom-3 right-3" label={t("home.ecosystem.metrics.aiMentor")} />

      {/* Desktop / tablet: SVG ring */}
      <div className="hidden md:block p-6">
        <svg
          viewBox={`0 0 ${CENTER * 2} ${CENTER * 2}`}
          role="img"
          aria-labelledby="eco-title eco-desc"
          className="w-full h-auto"
        >
          <title id="eco-title">{t("home.ecosystem.svgTitle")}</title>
          <desc id="eco-desc">{t("home.ecosystem.svgDesc")}</desc>

          <defs>
            <linearGradient id="brandGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="hsl(var(--accent))" />
            </linearGradient>
            <filter id="ecoNoise">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" />
              <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.04 0" />
            </filter>
            <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity="0.35" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </radialGradient>
          </defs>

          <rect width="100%" height="100%" filter="url(#ecoNoise)" opacity="0.5" />

          {/* connectors */}
          <g fill="none" stroke="url(#brandGrad)" strokeWidth="2" strokeLinecap="round">
            {NODES.map(({ key, angle }) => {
              const end = polar(angle, RADIUS - PILL_H / 2 - 4);
              const ctrl = polar(angle - 14, RADIUS * 0.55);
              const d = `M ${CENTER} ${CENTER} Q ${ctrl.x} ${ctrl.y} ${end.x} ${end.y}`;
              return (
                <path key={key} d={d} strokeDasharray="4 8" opacity="0.85">
                  {!reduced && (
                    <animate
                      attributeName="stroke-dashoffset"
                      from="0"
                      to="-48"
                      dur="6s"
                      repeatCount="indefinite"
                    />
                  )}
                </path>
              );
            })}
          </g>

          {/* hub glow */}
          <circle cx={CENTER} cy={CENTER} r={HUB_R + 32} fill="url(#hubGlow)" />

          {/* hub */}
          <g>
            <circle
              cx={CENTER} cy={CENTER} r={HUB_R}
              fill="hsl(var(--card))"
              stroke="url(#brandGrad)"
              strokeWidth="2.5"
            />
            <foreignObject
              x={CENTER - 16} y={CENTER - 22} width="32" height="32"
            >
              <Lightbulb
                className="text-primary"
                size={32}
                strokeWidth={2.2}
              />
            </foreignObject>
            <text
              x={CENTER}
              y={CENTER + 22}
              textAnchor="middle"
              fontSize="13"
              fontWeight="600"
              fill="hsl(var(--foreground))"
            >
              {t("home.ecosystem.hub")}
            </text>
          </g>

          {/* orbit nodes */}
          {NODES.map(({ key, icon: Icon, angle }) => {
            const p = polar(angle, RADIUS);
            const x = p.x - PILL_W / 2;
            const y = p.y - PILL_H / 2;
            return (
              <g key={key}>
                <rect
                  x={x} y={y}
                  width={PILL_W} height={PILL_H}
                  rx="14"
                  fill="hsl(var(--card))"
                  stroke="hsl(var(--border))"
                  strokeWidth="1"
                />
                <foreignObject
                  x={x + 14} y={p.y - 11} width="22" height="22"
                >
                  <Icon className="text-primary" size={22} strokeWidth={2.1} />
                </foreignObject>
                <text
                  x={x + 44}
                  y={p.y + 5}
                  fontSize="13"
                  fontWeight="600"
                  fill="hsl(var(--foreground))"
                >
                  {nodeLabel(key)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Mobile: stacked fallback */}
      <div className="md:hidden p-6">
        <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full border-2 border-primary/40 bg-card shadow-lg">
          <Lightbulb className="h-9 w-9 text-primary" strokeWidth={2.2} />
        </div>
        <div className="mb-4 text-center text-sm font-semibold text-foreground">
          {t("home.ecosystem.hub")}
        </div>
        <div
          aria-hidden
          className="mx-auto mb-4 h-6 w-px"
          style={{ background: "var(--brand-gradient)" }}
        />
        <ul className="grid grid-cols-2 gap-2.5">
          {NODES.map(({ key, icon: Icon }) => (
            <li
              key={key}
              className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5"
            >
              <Icon className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.1} />
              <span className="truncate text-xs font-semibold text-foreground">
                {nodeLabel(key)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MetricChip({ className, label }: { className: string; label: string }) {
  return (
    <div
      className={`pointer-events-none absolute z-10 rounded-full border border-border bg-card/80 px-2.5 py-1 text-[10px] font-semibold text-foreground shadow-sm backdrop-blur ${className}`}
    >
      {label}
    </div>
  );
}
